import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Col, Input, Row, Statistic, Table, Typography } from "antd";
import {
  BookOutlined,
  CheckCircleOutlined,
  FileTextOutlined,
  LoginOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { createLmsApi, getLmsErrorMessage, getLmsListItems } from "../../lms/api";
import {
  LmsErrorAlert,
  LmsPageHeader,
  LmsStatusTag,
  formatDate,
  formatDateTime,
} from "../../lms/components/LmsPrimitives";

const { Text } = Typography;

const LmsDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [activities, setActivities] = useState([]);
  const [expandedStudentKeys, setExpandedStudentKeys] = useState([]);
  const [activityError, setActivityError] = useState("");
  const [activitySearch, setActivitySearch] = useState("");
  const [activityPagination, setActivityPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [activityLoading, setActivityLoading] = useState(false);
  const loadActivities = useCallback(async (page = 1) => {
    setActivityLoading(true);
    const response = await createLmsApi(navigate).learnerActivity({ page, pageSize: 20, search: activitySearch || undefined, ordering: "-last_activity_at" });
    if (response?.ok) {
      const result = getLmsListItems(response.data);
      setActivities(result.items);
      setExpandedStudentKeys([]);
      setActivityPagination({ current: page, pageSize: 20, total: result.count });
      setActivityError("");
    } else {
      setActivities([]);
      setExpandedStudentKeys([]);
      setActivityError(getLmsErrorMessage(response, "Unable to load learner activity"));
    }
    setActivityLoading(false);
  }, [navigate, activitySearch]);
  useEffect(() => { loadActivities(1); }, [loadActivities]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError("");
    const api = createLmsApi(navigate);
    const response = await api.dashboard();
    if (response?.ok) {
      setDashboard(response.data || {});

    } else {
      setError(getLmsErrorMessage(response, "Unable to load the LMS dashboard"));
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const cards = [
    {
      title: "Active sessions",
      value: dashboard?.activeSessions || 0,
      icon: <BookOutlined />,
      color: "#1677ff",
    },
    {
      title: "Content uploads",
      value: dashboard?.totalContentUploads || 0,
      icon: <FileTextOutlined />,
      color: "#13c2c2",
    },
    {
      title: "Live sessions",
      value: dashboard?.totalLiveSessions || 0,
      icon: <VideoCameraOutlined />,
      color: "#722ed1",
    },
    {
      title: "Student activities",
      value: dashboard?.totalStudentActivities || 0,
      icon: <LoginOutlined />,
      color: "#fa8c16",
    },
    {
      title: "Reports approved",
      value: dashboard?.weeklyReportsApproved || 0,
      icon: <CheckCircleOutlined />,
      color: "#52c41a",
    },
  ];

  const columns = [
    { title: "Session", dataIndex: "title", key: "title" },
    { title: "Type", dataIndex: "session_type", key: "session_type" },
    { title: "Academic year", dataIndex: "academicYear", key: "academicYear" },
    { title: "Term", dataIndex: "termName", key: "termName" },
    {
      title: "Dates",
      key: "dates",
      render: (_, record) => `${formatDate(record.start_date)} – ${formatDate(record.end_date)}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => <LmsStatusTag status={status} />,
    },
    {
      title: "Open",
      key: "open",
      render: (_, record) => (
        <Link to={`/admin/lms/sessions?session=${record.id}`}>View session</Link>
      ),
    },
  ];

  const activityColumns = [
    { title: "Session", dataIndex: "sessionTitle", key: "sessionTitle" },
    { title: "Content", dataIndex: "contentTitle", key: "contentTitle", render: (value) => value || "—" },
    { title: "Activity", dataIndex: "activityTypeDisplay", key: "activityTypeDisplay" },
    { title: "Count", dataIndex: "count", key: "count" },
    { title: "Last activity", dataIndex: "lastActivityAt", key: "lastActivityAt", render: (value) => formatDateTime(value) },
  ];

  const toggleStudentActivities = (studentID) => {
    setExpandedStudentKeys((current) => current.includes(studentID)
      ? current.filter((key) => key !== studentID)
      : [...current, studentID]);
  };

  return (
    <div style={{ padding: 24 }}>
      <LmsPageHeader
        title="LMS dashboard"
        description="A live overview of revision programmes, learning content and learner engagement."
      />
      <LmsErrorAlert error={error} onClose={() => setError("")} />

      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        {cards.map((card) => (
          <Col xs={24} sm={12} lg={8} xl={Math.max(4, Math.floor(24 / cards.length))} key={card.title}>
            <Card>
              <Statistic
                title={card.title}
                value={card.value}
                prefix={React.cloneElement(card.icon, { style: { color: card.color } })}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Card
        title="Active sessions"
        extra={<Text type="secondary">{dashboard?.totalSessions || 0} sessions in total</Text>}
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={dashboard?.activeSessionDetails || []}
          loading={loading}
          pagination={false}
          scroll={{ x: 900 }}
          locale={{ emptyText: "No active LMS sessions" }}
        />
      </Card>

      <Card title="Learner activity" style={{ marginTop: 20 }}>
        <Input.Search allowClear placeholder="Search learner or session" value={activitySearch} onChange={(event) => setActivitySearch(event.target.value)} style={{ maxWidth: 360, marginBottom: 16 }} />
        <LmsErrorAlert error={activityError} onClose={() => setActivityError("")} />
        <Table
          rowKey="studentID"
          columns={[
            {
              title: "Learner",
              dataIndex: "studentName",
              key: "studentName",
              render: (name, record) => (
                <Button
                  type="link"
                  style={{ padding: 0, height: "auto", whiteSpace: "normal", textAlign: "left" }}
                  aria-expanded={expandedStudentKeys.includes(record.studentID)}
                  onClick={() => toggleStudentActivities(record.studentID)}
                >
                  {name || `Student ${record.studentID}`}
                </Button>
              ),
            },
            { title: "Class", dataIndex: "studentClass", key: "studentClass" },
            { title: "Sessions", dataIndex: "sessionCount", key: "sessionCount" },
            { title: "Total activities", dataIndex: "totalActivities", key: "totalActivities" },
            { title: "Last activity", dataIndex: "lastActivityAt", key: "lastActivityAt", render: (value) => formatDateTime(value) },
          ]}
          dataSource={activities}
          expandable={{
            expandedRowKeys: expandedStudentKeys,
            onExpandedRowsChange: setExpandedStudentKeys,
            expandedRowRender: (record) => (
              <Table
                rowKey="id"
                columns={activityColumns}
                dataSource={record.activities || []}
                pagination={false}
                size="small"
                scroll={{ x: 750 }}
                locale={{ emptyText: "No activities recorded for this learner" }}
              />
            ),
          }}
          loading={activityLoading}
          pagination={{ ...activityPagination, showSizeChanger: false }}
          onChange={(next) => loadActivities(next.current)}
          scroll={{ x: 850 }}
          locale={{ emptyText: "No learner activity recorded" }}
        />
      </Card>
    </div>
  );
};

export default LmsDashboard;
